import { ReactElement, useCallback } from "react";
import {
	SliderWithToggle,
	WeekSlider,
	WeekView
} from "client/Components";
import { IntegrationTileCalendarViewKind } from "client/Api/Workspace/workspace";
import { useWeekDays, WeekDay } from "client/Hooks";
import { TimeZone } from "client/DateAndTime/timezones";
import { LiveTileContent } from "../../LiveTileContent";
import { PlanningItemModel, PlanningLiveInformationState } from "./PlanningLiveTileContent";
import { PlanningScheduleContent } from "./PlanningScheduleContent";
import { PlanningCard } from "./PlanningCard";
import styles from "./PlanningWeekSchedule.module.less";

export type PlanningWeekScheduleProps = {
	state: PlanningLiveInformationState;
	onReload: () => void;
	appName: string;
	date: Date;
	onDateChange: (date: Date) => void;
	isTodayButtonAvailable: boolean;
	timezone: TimeZone;
	onViewChange: (view: IntegrationTileCalendarViewKind) => void;
	showHoursColumn: boolean;
};

export const PlanningWeekSchedule = (props: PlanningWeekScheduleProps): ReactElement => {
	const {
		state,
		onReload,
		appName,
		date,
		onDateChange,
		isTodayButtonAvailable,
		timezone,
		onViewChange,
		showHoursColumn
	} = props;

	const toggleClickHandler = useCallback(
		(): void => {
			onViewChange(IntegrationTileCalendarViewKind.Day);
		},
		[onViewChange]
	);

	const days: readonly WeekDay[] = useWeekDays(
		date,
		"culture",
		timezone
	);

	if (days.length === 0) {
		throw new Error("Week does not contain any day.");
	}

	const startOfWeek: Date = days[0].date;

	return (
		<LiveTileContent.ContentStack>
			<div className={styles.sliderContainer}>
				<SliderWithToggle>
					<SliderWithToggle.Slider>
						<WeekSlider
							mode="culture"
							startDate={startOfWeek}
							onChange={onDateChange}
							isTodayButtonAvailable={isTodayButtonAvailable}
							trackingId="planning-week-slider"
							timezone={timezone}
						/>
					</SliderWithToggle.Slider>
					<SliderWithToggle.Toggle
						icon="calendar-day"
						onClick={toggleClickHandler}
					/>
				</SliderWithToggle>
			</div>

			<LiveTileContent.BusyIndicatorContainer>
				<PlanningScheduleContent
					state={state}
					appName={appName}
					onReload={onReload}
					renderItems={(items: readonly PlanningItemModel[]): ReactElement => (
						<Week
							days={days}
							items={items}
							appName={appName}
							timezone={timezone}
							showHoursColumn={showHoursColumn}
						/>
					)}
				/>
			</LiveTileContent.BusyIndicatorContainer>
		</LiveTileContent.ContentStack>
	);
};
PlanningWeekSchedule.displayName = "PlanningWeekSchedule";

type WeekProps = {
	days: readonly WeekDay[];
	items: readonly PlanningItemModel[];
	appName: string;
	timezone: TimeZone;
	showHoursColumn: boolean;
};

const Week = (props: WeekProps): ReactElement => {
	const {
		days,
		items,
		appName,
		timezone,
		showHoursColumn
	} = props;

	const renderCard = (item: PlanningItemModel): ReactElement => (
		<PlanningCard
			item={item}
			appName={appName}
			timezone={timezone}
		/>
	);

	return (
		<LiveTileContent.Scrollable>
			<WeekView
				mode={showHoursColumn ? "hours" : "stacked"}
				days={days}
				items={items}
				itemKey="uniqueId"
				startDateTimeRetriever={startDateTimeRetriever}
				endDateTimeRetriever={endDateTimeRetriever}
				timezone={timezone}
			>
				{renderCard}
			</WeekView>
		</LiveTileContent.Scrollable>
	);
};
Week.displayName = "Week";

function startDateTimeRetriever(item: PlanningItemModel): string {
	return item.startDateTime.value;
}

function endDateTimeRetriever(item: PlanningItemModel): string {
	return item.endDateTime.value;
}
